import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { fetchTransactionAnalyticsAsync } from "../../../redux/features/analyticsSlice";
import moment from "moment";
import CustomTable from "../../../components/Tables/CustomTable";
import WhiteTitledCard from "../../../components/cards/WhiteTitledCard";
import OverviewCard from "../../../components/cards/OverviewCard";
import FilterByButton from "../../ui/FilterButton";
import SearchInput from "../../../components/form/SearchInput";
import {
  revenueAnalyticsTableColumns,
  transactionSummaryRows,
  transactionStatusOptions,
} from "./tableUtils";
import {
  DollarSign,
  TrendingDown,
  TrendingUp,
  ShoppingCart,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const TransactionsTable = () => {
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const [status, setStatus] = useState<string>("");
  const [filter, setFilter] = useState<string>("");
  const { loading, transactions } = useAppSelector((state) => state.analytics);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(
      fetchTransactionAnalyticsAsync({
        page: page + 1,
        limit,
        startDate: new Date(moment().startOf("year").toISOString()),
        endDate: new Date(moment().endOf("year").toISOString()),
        status,
        filter,
      }),
    );
  }, [page, limit, dispatch, status, filter]);

  const transactionCount = transactions?.data?.meta?.totalItems ?? 0;

  const tableData = {
    columns: revenueAnalyticsTableColumns,
    rows: transactionSummaryRows(transactions?.data),
  };

  return (
    <div className="p-6 flex flex-col gap-6">
      {/* Header */}
      <p className="text-2xl font-bold">Overview</p>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <OverviewCard
          title="Total revenue"
          value="₦300,000,000.00"
          icon={TrendingUp}
          iconBg="bg-purple-600"
          isLoading={loading}
          loadingColor="#7C3AED"
          change="↑ 3.5%"
          isPositive={true}
          changeLabel="vs last year"
        />
        <OverviewCard
          title="Revenue withdrawal"
          value="₦100,000,000.00"
          icon={TrendingDown}
          iconBg="bg-purple-600"
          isLoading={loading}
          loadingColor="#7C3AED"
          change="↓ 1.5%"
          isPositive={false}
          changeLabel="vs last year"
        />
        <OverviewCard
          title="Revenue deposit"
          value="₦100,000,000.00"
          icon={DollarSign}
          iconBg="bg-purple-600"
          isLoading={loading}
          loadingColor="#7C3AED"
          change="↑ 3.5%"
          isPositive={true}
          changeLabel="vs last year"
        />
        <OverviewCard
          title="Revenue purchase"
          value="₦100,000,000.00"
          icon={ShoppingCart}
          iconBg="bg-purple-600"
          isLoading={loading}
          loadingColor="#7C3AED"
          change="↑ 3.5%"
          isPositive={true}
          changeLabel="vs last year"
        />
      </div>

      {/* Transactions Table Section */}
      <WhiteTitledCard>
        <div className="p-4">
          {/* Header with count and Add agent button */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <p className="text-lg font-bold">All transactions</p>
              <span className="bg-purple-100 text-purple-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                {transactionCount}
              </span>
            </div>
            <button
              onClick={() => navigate("/agent-management")}
              className="bg-primary text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:opacity-90 transition"
            >
              Add agent
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 mb-4">
            <FilterByButton
              options={transactionStatusOptions}
              onSelect={(value) => {
                setStatus(value);
                setPage(0);
              }}
              buttonLabel="Filter by"
              defaultValue={status}
            />
            <SearchInput
              placeholder="Search"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setPage(0);
              }}
            />
          </div>

          <CustomTable
            data={tableData}
            noRecordText="Record is Empty"
            loading={loading}
            showFooter={true}
            totalPages={transactions?.data?.meta?.totalPages}
            itemsPerPage={transactions?.data?.meta?.itemCount}
            currentPage={page}
            onPageChange={(value: number) => setPage(value)}
            onLimitChange={(value: number) => {
              setLimit(value);
              setPage(0);
            }}
          />
        </div>
      </WhiteTitledCard>
    </div>
  );
};

export default TransactionsTable;
